import { paginateRows } from "./listQueryUtils";
import {
  filterStatementsForInvoicing,
  getStatementRows,
} from "./invoiceDemoHelpers";
import {
  enrichStatementWithSubuserAccounting,
  findSubuserStatementRow,
  getSubuserStatementRows,
  queueSubuserReportForStatement,
} from "./subuserAccountingDemoHelpers";
import { upsertSubuserStatementOverride } from "./subuserAccountingSessionStore";

function enrichSubuserStatementRow(row) {
  const sid = Number(row?.id) || 0;
  return {
    visibility: row?.visibility ?? (sid % 3 === 0 ? "active" : "inactive"),
    subuser_statement_emails_sent:
      row?.subuser_statement_emails_sent ?? sid % 5 === 0,
    subuser_report__parent_report: row?.subuser_report__parent_report ?? null,
    ...row,
  };
}

function filterSubuserStatements(rows, queryParams) {
  let filtered = (rows || []).map(enrichSubuserStatementRow);

  const parentReport = queryParams?.get("subuser_report__parent_report");
  if (parentReport != null && parentReport !== "") {
    filtered = filtered.filter(
      row => String(row.subuser_report__parent_report || "") === String(parentReport),
    );
  }

  const visibility = queryParams?.get("visibility");
  if (visibility != null && visibility !== "") {
    filtered = filtered.filter(row => row.visibility === visibility);
  }

  return filterStatementsForInvoicing(filtered, queryParams);
}

/** Parent statements list with Subuser Acc column fields. */
export function resolveStatementsList(queryParams) {
  const rows = getStatementRows().map(enrichStatementWithSubuserAccounting);
  const filtered = filterStatementsForInvoicing(rows, queryParams);
  return paginateRows(filtered, queryParams);
}

export function resolveSubuserStatementsList(queryParams, subUserIdFromPath = null) {
  const rows = getSubuserStatementRows();
  const filtered = filterSubuserStatements(rows, queryParams);
  return paginateRows(filtered, queryParams, { subUserIdFromPath });
}

export function handleQueueSubuserReport(statementId) {
  const row = getStatementRows().find(
    entry => String(entry.id) === String(statementId),
  );
  if (!row) {
    throw new Error("Statement not found.");
  }
  return queueSubuserReportForStatement(row);
}

export function handleUpdateSubuserStatementStatus(payload) {
  const statementId = payload?.id;
  if (statementId == null || statementId === "") {
    throw new Error("Statement id is required.");
  }

  const row = findSubuserStatementRow(statementId);
  if (!row) {
    throw new Error("Statement not found.");
  }

  const nextStatus = Number(row.status) === 0 ? 1 : 0;
  upsertSubuserStatementOverride(statementId, { status: nextStatus });
  return { detail: "Statement status updated." };
}

export function handleUpdateSubuserStatementVisibility(payload) {
  const statementId = payload?.id;
  if (statementId == null || statementId === "") {
    throw new Error("Statement id is required.");
  }

  const row = enrichSubuserStatementRow(findSubuserStatementRow(statementId));
  if (!row?.id) {
    throw new Error("Statement not found.");
  }

  const nextVisibility = row.visibility === "active" ? "inactive" : "active";
  upsertSubuserStatementOverride(statementId, { visibility: nextVisibility });
  return { detail: "Statement visibility updated." };
}

export function handleBulkMarkSubuserStatementsVisible(payload) {
  const parentReport = payload?.parent_report;
  let targets = getSubuserStatementRows().map(enrichSubuserStatementRow);

  if (parentReport != null && parentReport !== "") {
    targets = targets.filter(
      row => String(row.subuser_report__parent_report || "") === String(parentReport),
    );
  }

  targets.forEach(row => {
    upsertSubuserStatementOverride(row.id, { visibility: "active" });
  });

  return {
    detail: `${targets.length} statement(s) marked as visible.`,
  };
}

export function handleBulkSendSubuserStatementEmails(payload) {
  const parentReport = payload?.parent_report;
  let targets = getSubuserStatementRows()
    .map(enrichSubuserStatementRow)
    .filter(row => !row.subuser_statement_emails_sent);

  if (parentReport != null && parentReport !== "") {
    targets = targets.filter(
      row => String(row.subuser_report__parent_report || "") === String(parentReport),
    );
  }

  targets.forEach(row => {
    upsertSubuserStatementOverride(row.id, { subuser_statement_emails_sent: true });
  });

  return {
    detail: "Emails queued for all relevant statements.",
  };
}
